import { useMemo, useState } from 'react';
import { QueryKey, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { SelectProps, Skeleton } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import useDebounce from '@/hooks/useDebounce';

type QuerySelectOption<T extends QueryKey = QueryKey> = {
	queryKey: T;
	queryFn: (page: number, key: [...T, string]) => Promise<DefaultOptionType[]>;
	infinite?: boolean;
	enabled?: boolean;
	/** Minimum keyword length before a search request is sent. Default: 0 (no minimum). */
	minSearchLength?: number;
};

export default function useQuerySelect<T extends QueryKey = QueryKey>({
	queryKey,
	queryFn,
	infinite = true,
	enabled = false,
	minSearchLength = 0,
}: QuerySelectOption<T>) {
	const [keyword, setKeyword] = useState('');
	const queryClient = useQueryClient();

	const { data, isLoading, isFetching, fetchNextPage } = useInfiniteQuery({
		queryKey: [...queryKey, keyword] as [...T, string],
		queryFn: async ({ pageParam, queryKey }) => queryFn(pageParam, queryKey),
		enabled,
		initialPageParam: 1,
		getNextPageParam: (_, __, b) => ++b,
	});

	const options = useMemo(() => {
		if (!data) return [];
		return ([] as DefaultOptionType[]).concat(
			...data.pages.map((page) => page),
		);
	}, [data]);

	const fetchNextPageDebounced = useDebounce(fetchNextPage, 500);

	return {
		loading: isLoading || isFetching,
		notFoundContent: isLoading ? (
			<Skeleton
				active
				title={false}
				paragraph={{
					rows: 7,
				}}
			/>
		) : undefined,
		showSearch: true,
		allowClear: true,
		filterOption: infinite
			? false
			: (input, option) => {
					return option?.label
						?.toLocaleString()
						.toLocaleLowerCase()
						.includes(input.toLocaleLowerCase());
				},
		options,
		onFocus: () => {
			if (!options.length) fetchNextPage();
		},
		onSelect: () => {
			setKeyword('');
		},
		onSearch: infinite
			? async (value) => {
					// Enforce minimum keyword length — don't fetch until threshold is met.
					if (value.length > 0 && value.length < minSearchLength) return;
					// Clear cached pages for the incoming keyword so fetchNextPage always starts at page 1.
					// Without this, cached pages from a prior search rehydrate and getNextPageParam
					// returns the wrong next page (e.g. 3 instead of 1).
					queryClient.removeQueries({
						queryKey: [...queryKey, value] as [...T, string],
						exact: true,
					});
					await setKeyword(value);
					if (value && infinite) fetchNextPageDebounced();
				}
			: undefined,
		onPopupScroll: (e) => {
			if (!infinite) return;
			const element = e.target as HTMLDivElement;
			const scrollPercentage =
				((element.scrollTop + element.clientHeight) / element.scrollHeight) *
				100;

			if (scrollPercentage >= 90 && scrollPercentage <= 100) {
				fetchNextPage();
			}
		},
		fetchNextPage,
	} as SelectProps & { fetchNextPage: typeof fetchNextPage };
}
